import type { Vendor } from '@pedestal-panels/contracts';

/**
 * Hand-rolled hash router.
 *
 * The routing surface is small (a single shape: `#/panels/<vendor>/<model>`),
 * so a library would be overkill. This parser is deliberately strict —
 * unknown vendors return `null` so the caller can render an empty state
 * rather than load the wrong panel.
 */

export interface PanelRoute {
  readonly vendor: Vendor;
  readonly model: string;
}

const VENDORS = new Set<Vendor>([
  'sony',
  'grassvalley',
  'panasonic',
  'blackmagic',
  'skaarhoj',
  'cyanview',
]);

export function parseRoute(hash: string): PanelRoute | null {
  // Strip the leading `#`, then split path segments and reject anything that
  // does not exactly match `/panels/<vendor>/<model>`.
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  const parts = trimmed.split('/').filter((p) => p.length > 0);
  if (parts.length !== 3 || parts[0] !== 'panels') return null;
  const vendor = parts[1];
  const model = parts[2];
  if (vendor === undefined || model === undefined) return null;
  if (!VENDORS.has(vendor as Vendor)) return null;
  return { vendor: vendor as Vendor, model };
}

export function formatRoute(route: PanelRoute): string {
  return `#/panels/${route.vendor}/${route.model}`;
}
